import { FC, useState } from "react";
import { Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { APIProvider } from "@vis.gl/react-google-maps";
import { PlaceAutocomplete } from "../../PlaceAutocomplete";
import { useAuth, useStore } from "../../../hooks";
import { StoreCustomerAddress } from "../../../services";
import { extractAddressDetails } from "../../../helpers";
import { CustomerAddressModel } from "../../../models";
import { Toast } from "../../Toast";

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

type Props = {
  show: boolean;
  handleSuccess?: (data: CustomerAddressModel) => void;
  handleClose: () => void;
};

const AddAddressModal: FC<Props> = ({ show, handleSuccess, handleClose }) => {
  const { storeId } = useStore();
  const { customerId } = useAuth();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPlace, setSelectedPlace] =
    useState<google.maps.places.PlaceResult | null>(null);
  const [addressType, setAddressType] = useState<string>("");

  const handleSubmit = async () => {
    if (!selectedPlace) {
      Toast.error("Please select a location");
      return;
    }

    if (!addressType) {
      Toast.error("Please choose an address type");
      return;
    }

    setIsLoading(true);

    try {
      const { address_street, city, state, postal_code, latitude, longitude } =
        extractAddressDetails(selectedPlace);
      const { status, message, data } = await StoreCustomerAddress({
        store_id: storeId,
        customer_id: customerId,
        address: {
          address_name: addressType,
          address_street: address_street,
          city_name: city,
          state_code: state,
          zip_code: postal_code,
          latitude: latitude?.toString(),
          longitude: longitude?.toString(),
        },
      });

      if (status) {
        Toast.info(message);
        handleSuccess && handleSuccess(data);
      } else Toast.error(message);
    } catch (e) {
      console.error("Unable to save address:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const addressTypes = [
    { id: 1, label: "Home" },
    { id: 2, label: "Work" },
    { id: 3, label: "Other" },
  ];

  return (
    <Modal
      className="order_option_popup"
      show={show}
      onHide={handleClose}
      fullscreen="sm-down"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Add Address</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="Profile_main">
          <div className="order_option_input address_icon mb-3">
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
              <PlaceAutocomplete onPlaceSelect={setSelectedPlace} />
              {/* <GoogleMap selectedPlace={selectedPlace} /> */}
            </APIProvider>
          </div>

          <Row className="mb-3">
            {addressTypes.map((item) => (
              <Col key={item.id} sm={12}>
                <Form.Check
                  id={`address_type_${item.id}`}
                  label={item.label}
                  name="address_type"
                  type="radio"
                  value={item.label}
                  onChange={() => setAddressType(item.label)}
                />
              </Col>
            ))}
          </Row>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? (
            <Spinner animation="border" variant="light" size="sm" />
          ) : (
            "Submit"
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddAddressModal;
